import { Component, inject, Optional } from '@angular/core';
import { AppUser, AuthService, EMPTY } from '@app/components/login.component';
import { Role } from '@app/shared/models/role';
import { isActionOnJedinicaDisabled } from '@app/shared/util/jedinica-gradiva-security-utils';
import { OpenArhivWindowActionUtil } from '@app/shared/util/open-arhiv-window-util';
import {
  ActionWindowParams,
  DEFAULT_ITEM_VIEW,
  EMPTY_ACTION_RESULT,
  Item,
  ItemActionResult,
  ItemUtil,
  ListView,
  ListViewProviderDirective,
  NotificationService,
  NtapGlobalConstants,
  Util,
  ViewState,
  ViewWindow,
  WindowRegistry
} from '@ntap/core';
import { ViewResult } from '@ntap/core/lib/view/view-result';
import { PrimeNGButtonNewItemViewActionComponent } from '@ntap/primeng';
import {
  filter,
  first,
  map,
  mergeMap,
  Observable,
  of,
  tap,
  withLatestFrom
} from 'rxjs';

@Component({
  selector: 'app-new-jedinica-gradiva-action',
  templateUrl: './new-jedinica-gradiva-action.component.html',
  styleUrls: ['./new-jedinica-gradiva-action.component.scss']
})
export class NewJedinicaGradivaActionComponent extends PrimeNGButtonNewItemViewActionComponent {
  actionState$: Observable<{
    disabled: boolean;
    label: string;
    tooltip: string;
  }> = of({
    disabled: false,
    label: 'Dodaj',
    tooltip: ''
  });
  label = 'Dodaj zbirku';

  // TODO napraviti i direktivu za skrivanje funkcionalnosti?
  private static allowedRoles: Role[] = [
    'Voditelj popisa fondova i zbirki',
    'Superuser',
    'Administrator arhiva',
    'Administrator posjednika',
    'Glavni administrator'
  ];

  private authService = inject(AuthService);

  constructor(
    protected override notificationService: NotificationService,
    protected override windowRegistry: WindowRegistry,
    @Optional() protected override window: ViewWindow,
    protected listViewProvider: ListViewProviderDirective<ListView>
  ) {
    super(notificationService, windowRegistry, window, listViewProvider);
    this.actionState$ = this.listViewProvider.selectedItems$.pipe(
      withLatestFrom(this.authService.user$),
      map(([selectedItems, user]) =>
        this.calculateActionState(selectedItems, user)
      )
    );
  }

  private calculateActionState(
    selectedItems: object[],
    user: AppUser | EMPTY
  ): { disabled: boolean; label: string; tooltip: string } {
    let tooltip = this.action.description;
    const userIsRoleThatCanAlwaysAdd = AuthService.userHasAnyRole(
      user,
      NewJedinicaGradivaActionComponent.allowedRoles
    );
    const label =
      !selectedItems || selectedItems.length == 0
        ? 'Dodaj fond ili zbirku'
        : 'Dodaj';
    let disabled = false;

    if (!selectedItems || selectedItems.length === 0) {
      disabled = !userIsRoleThatCanAlwaysAdd; // Ako nema odabranih stavki, akcija je onemogućena osim za ovlaštene uloge
      tooltip = disabled
        ? 'Nemate prava za dodavanje bez odabira jedinice.'
        : tooltip;
    } else {
      // Provjera svih selektiranih stavki
      const checkResults = selectedItems.map((item) =>
        isActionOnJedinicaDisabled(item, user as AppUser)
      );
      const hasDisabledItem = checkResults.some((result) => result.disabled);

      if (hasDisabledItem) {
        disabled = true;
        // Uzmi prvu poruku iz onemogućene stavke kao tooltip (ili prilagođeno pravilo za kombiniranje poruka)
        const firstDisabledMessage =
          checkResults.find((result) => result.disabled)?.message ||
          'Neke jedinice gradiva ne dopuštaju akciju.';
        tooltip = firstDisabledMessage;
      } else if (selectedItems.length > 1 && !userIsRoleThatCanAlwaysAdd) {
        disabled = true;
      }
    }
    return { disabled, label, tooltip };
  }

  protected override doExecuteWithItem(
    item: Item
  ): Observable<ItemActionResult> {
    const keyItem = ItemUtil.extractItemKey(item);
    return this.listViewProvider.selectedItems$.pipe(
      first(),
      mergeMap((selectedItems) => {
        if (!!selectedItems && selectedItems.length > 1) {
          throw new Error('Only a single parent object can be selected!');
        }

        const parentItem: any =
          (!!selectedItems && selectedItems.length > 0 && selectedItems[0]) ||
          {};

        const parentItemType =
          parentItem[NtapGlobalConstants.ATTR_$itemType] ||
          parentItem[NtapGlobalConstants.ATTR_$mainType];

        const parentItemIdAttrName = Util.getFullAttrName(
          parentItemType,
          NtapGlobalConstants.ATTR_id
        );

        const parentItemId = parentItem[parentItemIdAttrName];

        const parentItemProps =
          (parentItemId && {
            $parentId: parentItemId,
            $parentType: parentItemType,
            $itemType: parentItemType
          }) ||
          {};

        const viewState = Object.assign({}, keyItem, parentItemProps);

        return this.doOpenWindow(viewState).pipe(
          filter((viewResult) => this.acceptResult(viewResult)),
          tap(() => this.log('result ACCEPTED')),
          map(
            (viewResult) =>
              new ItemActionResult(EMPTY_ACTION_RESULT, viewResult.viewData[0])
          )
        );
      })
    );
  }

  private doOpenWindow(viewState: ViewState): Observable<ViewResult> {
    return OpenArhivWindowActionUtil.open(
      this.getActionWindowParams(),
      viewState
    );
  }

  protected override getActionWindowParams(): ActionWindowParams {
    this.doSetDefaultActionViewName();
    const actionWindowParams = OpenArhivWindowActionUtil.createParams(
      this.action,
      this.windowRegistry,
      this.getWindowTarget()
    );

    return actionWindowParams;
  }

  private doSetDefaultActionViewName() {
    if (!this.action.$openViewName) {
      this.action.$openViewName = DEFAULT_ITEM_VIEW;
    }
  }
}
